import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { CategoryColorsProvider } from './contexts/CategoryColorsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Transactions from './pages/Transactions';
import Reconciliation from './pages/Reconciliation';
import AccountTypes from './pages/AccountTypes';
import Statement from './pages/Statement';
import Moderation from './pages/Moderation';
import RoadmapAdmin from './pages/RoadmapAdmin';
import SiteSettingsAdmin from './pages/SiteSettingsAdmin';
import Pricing from './pages/Pricing';
import AdminTiers from './pages/AdminTiers';
import CheckoutSuccess from './pages/CheckoutSuccess';
import Help from './pages/Help';
import About from './pages/About';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import AdminBlog from './pages/AdminBlog';
import EditPageContent from './pages/EditPageContent';
import AccountHealthCheck from './pages/AccountHealthCheck';
import AdminFeatureFlags from './pages/AdminFeatureFlags';
import AdminVat from './pages/AdminVat';
import AdminMetrics from './pages/AdminMetrics';
import AdminQAChecklist from './pages/AdminQAChecklist';
import AdminPreDeployChecklist from './pages/AdminPreDeployChecklist';
import AdminReleases from './pages/AdminReleases';
import AdminMessages from './pages/AdminMessages';
import AdminWaitlist from './pages/AdminWaitlist';
import AdminEmailTest from './pages/AdminEmailTest';
import CookieConsent from './components/CookieConsent';
import Contact from './pages/Contact';
import ChangePassword from './pages/ChangePassword';
import AccountSettings from './pages/AccountSettings';
import Security from './pages/Security';
import RootGate from './components/RootGate';
import { captureReferralFromUrl } from './utils/referralStorage';
import useClarity from './hooks/useClarity';
import './App.css';

function AppRouter() {
  const location = useLocation();

  // Capture ?ref= referral codes on any inbound URL into localStorage so
  // a later signup (potentially days later) still attributes correctly.
  // Cheap to run on every route change.
  React.useEffect(() => {
    captureReferralFromUrl();
  }, [location.pathname, location.search]);

  // Microsoft Clarity — gated by cookie consent + customer-only routes +
  // non-internal roles. No-op if any guard fails. See hooks/useClarity.js.
  useClarity();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/security" element={<Security />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      {/* /pricing and /help must be PUBLIC — anonymous visitors need to
          see tiers AND read FAQs before they sign up. Previously these
          were inside ProtectedRoute which forced /login redirect. */}
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/help" element={<Help />} />

      {/* Public root — landing/coming-soon for anon, redirect to /home for authed */}
      <Route path="/" element={<RootGate />} />

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/home" element={<Home />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/accounts" element={<Accounts />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/recurring" element={<Navigate to="/transactions" replace />} />
        <Route path="/reconcile" element={<Reconciliation />} />
        <Route path="/accounts/:accountId/statement" element={<Statement />} />
        <Route path="/settings/account-types" element={<AccountTypes />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/admin/moderation" element={<Moderation />} />
        <Route path="/admin/blog" element={<AdminBlog />} />
        <Route path="/admin/roadmap" element={<RoadmapAdmin />} />
        <Route path="/admin/tiers" element={<AdminTiers />} />
        <Route path="/admin/site-settings" element={<SiteSettingsAdmin />} />
        <Route path="/admin/edit/:slug" element={<EditPageContent />} />
        <Route path="/settings/health-check" element={<AccountHealthCheck />} />
        <Route path="/admin/feature-flags" element={<AdminFeatureFlags />} />
        <Route path="/admin/vat" element={<AdminVat />} />
        <Route path="/admin/metrics" element={<AdminMetrics />} />
        <Route path="/admin/qa-checklist" element={<AdminQAChecklist />} />
        <Route path="/admin/pre-deploy-checklist" element={<AdminPreDeployChecklist />} />
        <Route path="/admin/releases" element={<AdminReleases />} />
        <Route path="/admin/messages" element={<AdminMessages />} />
        <Route path="/admin/waitlist" element={<AdminWaitlist />} />
        <Route path="/admin/email-test" element={<AdminEmailTest />} />
        <Route path="/account/settings" element={<AccountSettings />} />
        <Route path="/account/change-password" element={<Navigate to="/account/settings?tab=security" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CategoryColorsProvider>
          <AppRouter />
          <CookieConsent />
          <Toaster position="top-right" richColors />
        </CategoryColorsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
